import { ButtonBase, Chip, styled } from '@mui/material'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { focusRing } from '@omnea-app/omui/focusRing'
import { insetBorder } from '@omnea-app/omui/insetBorder'
import { OrgLogo } from './OrgLogo'
import type { ButtonBaseProps } from '@mui/material'
import type { ReactNode } from 'react'

type OrgToggleProps = {
  orgName: string
  /** The signed-in user's name, read out with the organisation's. */
  userName: string
  /** The organisation's wordmark. */
  logoSrc?: string
  /** The wordmark drawn for dark backgrounds. */
  darkLogoSrc?: string
  /** Marks a QA organisation. */
  qa?: boolean
  /** The signed-in user's 24px avatar. */
  avatar: ReactNode
} & Pick<ButtonBaseProps, 'onClick' | 'aria-expanded' | 'aria-controls' | 'aria-haspopup'>

const Root = styled(ButtonBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing(2.5),
  height: '2.25rem',
  padding: theme.spacing(1, 1.5, 1, 4),
  borderRadius: theme.shape['radius-full'],
  boxShadow: insetBorder({ width: theme.strokeWidth.default, color: theme.palette.border.strong }),
  transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shortest }),
  '&:hover, &[aria-expanded="true"]': {
    backgroundColor: theme.palette.bg.interactive.hover,
  },
  '&.Mui-focusVisible': focusRing(theme),
}))

const Logo = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}))

/**
 * The organisation and account control in the app's top bar: the organisation's logo and the signed-in
 * user's avatar in one pill, opening the account menu.
 */
export const OrgToggle = forwardRef<HTMLButtonElement, OrgToggleProps>(function OrgToggle(
  { orgName, userName, logoSrc, darkLogoSrc, qa = false, avatar, ...buttonProps },
  ref
) {
  const { t } = useTranslation('common')
  // The logo and avatar don't read out, and the QA chip would otherwise only be seen.
  const label = t(qa ? 'org-toggle.label-qa' : 'org-toggle.label', { orgName, userName })

  return (
    <Root ref={ref} disableRipple {...buttonProps} aria-label={label}>
      <Logo>
        <OrgLogo name={orgName} src={logoSrc} darkSrc={darkLogoSrc} />
        {qa && <Chip label={t('qa')} />}
      </Logo>
      {avatar}
    </Root>
  )
})
